"use client";

import React from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";

const cormorant = {
    className: "font-cormorant font-serif",
};

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

export interface GuestiqoEpilogueBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    primaryBtnText?: string;
    primaryBtnLink?: string;
    secondaryBtnText?: string;
    secondaryBtnLink?: string;
    footnote?: string;
}

export default function GuestiqoEpilogueBlock({
    tag = "// Project Epilogue",
    title = "Create memorable stays, digitally.",
    description = "Explore the live production website, or interact directly with the active guest-facing sandbox demo app.",
    primaryBtnText = "Visit Website",
    primaryBtnLink = "https://guestiqo.uk/",
    secondaryBtnText = "Try Guest Demo",
    secondaryBtnLink = "https://demo.guestiqo.uk/",
    footnote = "Multi-tenant hosting enabled • CDN-cached assets loaded"
}: GuestiqoEpilogueBlockProps) {
    return (
        <section className="py-32 px-6 md:px-12 lg:px-24 bg-[#F2ECE4] border-t border-[#1C1C1C]/5 text-center relative overflow-hidden w-full" data-header-theme="dark">
            <div className="absolute inset-0 bg-[url('/images/ui-ux/glasstexture.png')] opacity-[0.02] pointer-events-none" />
            
            <div className="container mx-auto z-10 max-w-2xl relative">
                <motion.div {...fadeUp} className="space-y-8">
                    {tag && <span className="text-[10px] font-mono uppercase tracking-widest text-[#8C8276]">{tag}</span>}
                    {title && <h2 className={`${cormorant.className} text-4xl md:text-6xl font-light text-[#1C1C1C] leading-tight`}>{title}</h2>}
                    {description && (
                        <p className="text-[#5E5E5E] text-sm md:text-base leading-relaxed max-w-md mx-auto font-sans font-light">
                            {description}
                        </p>
                    )}
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
                        {primaryBtnText && primaryBtnLink && (
                            <a href={primaryBtnLink} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#1C1C1C] text-[#FDFBF7] hover:bg-[#2E2C29] transition-all duration-300 font-mono text-xs uppercase tracking-wider font-semibold rounded-none">
                                {primaryBtnText} <ExternalLink className="w-4 h-4" />
                            </a>
                        )}
                        {secondaryBtnText && secondaryBtnLink && (
                            <a href={secondaryBtnLink} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 border border-[#1C1C1C] text-[#1C1C1C] hover:bg-[#1C1C1C] hover:text-[#FDFBF7] transition-all duration-300 font-mono text-xs uppercase tracking-wider font-semibold rounded-none">
                                {secondaryBtnText}
                            </a>
                        )}
                    </div>

                    {footnote && (
                        <div className="text-[9px] font-mono text-[#8C8276] pt-6 uppercase tracking-widest">
                            {footnote}
                        </div>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
